import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { signOut } from "@/lib/auth";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { PageHeadline } from "@/components/ui/Headline";
import { OrganizerShell } from "@/components/layout/OrganizerShell";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { Logo } from "@/components/ui/Logo";
import { RoleSwitcher } from "@/components/RoleSwitcher";

/* -------------------------------------------------------------------------
 * Static informational pages — referenced by the global footer.
 * ----------------------------------------------------------------------- */

export function FaqPage() {
  return (
    <PublicLayout centered={false}>
      <StaticContainer>
        <Logo size="md" />
        <PageHeadline title="Frequently asked questions" className="mt-6" />
        <Card padding="md" className="mt-6">
          <FaqItem
            q="How long are pitches?"
            a="30 to 60 seconds, recorded as a circle video on your phone or laptop camera."
          />
          <FaqItem
            q="When are matches revealed?"
            a="The organizer picks the reveal time when creating the event — usually 2 hours before doors open. Reveal happens at the same instant for everyone."
          />
          <FaqItem
            q="Who sees my video?"
            a="Only registered participants of the same event. Videos and profile data are deleted 30 days after the event."
          />
          <FaqItem
            q="What happens if my pitch is flagged?"
            a="The AI flags pitches that look like ads, are too short, or miss the 'how I can help' part. The organizer reviews — you'll get an email if a re-record is needed."
          />
          <FaqItem
            q="Can I delete my pitch?"
            a="Yes — open Profile and tap Delete My Pitch. Your matches are cancelled and your profile leaves the feed immediately."
          />
        </Card>
      </StaticContainer>
    </PublicLayout>
  );
}

export function RulesPage() {
  return (
    <PublicLayout centered={false}>
      <StaticContainer>
        <Logo size="md" />
        <PageHeadline title="Platform rules" className="mt-6" />
        <Card padding="md" className="mt-6">
          <ul className="flex flex-col gap-3 text-[14px] text-[#475569] leading-relaxed">
            <li>· Pitches must be original, your own voice and face, no third-party promotions.</li>
            <li>· No advertising links or sales pitches inside the video — AI flags them.</li>
            <li>· Be respectful: rude, sexist, or aggressive behaviour gets you banned from the event.</li>
            <li>· Contact for matches must be a real, current channel where you can be reached.</li>
            <li>· Like only the people you actually want to meet — the system is built on mutual interest.</li>
          </ul>
        </Card>
      </StaticContainer>
    </PublicLayout>
  );
}

export function AboutPage() {
  return (
    <PublicLayout centered={false}>
      <StaticContainer>
        <Logo size="md" />
        <PageHeadline title="About Finally" className="mt-6" />
        <Card padding="md" className="mt-6">
          <p className="text-[15px] text-[#475569] leading-relaxed">
            Finally is a smart networking tool for offline events. Instead of randomly bumping into
            strangers, every participant records a short video pitch. AI builds a profile from it
            and surfaces the most relevant connections for everyone — and contacts open right
            before the event so the room arrives knowing who they want to talk to.
          </p>
          <p className="mt-4 text-[15px] text-[#475569] leading-relaxed">
            <strong className="text-[#1a1a1a]">People work with people.</strong> We make sure the
            right ones meet.
          </p>
        </Card>
      </StaticContainer>
    </PublicLayout>
  );
}

export function PrivacyPolicyPage() {
  return (
    <PublicLayout centered={false}>
      <StaticContainer>
        <Logo size="md" />
        <PageHeadline title="Privacy policy" className="mt-6" />
        <Card padding="md" className="mt-6">
          <p className="text-[14px] text-[#475569] leading-relaxed">
            We collect the minimum personal data needed to run the event: your name, email, role,
            company, contact for matches, and the pitch video. The data is visible only to other
            registered participants of the same event.
          </p>
          <p className="mt-3 text-[14px] text-[#475569] leading-relaxed">
            All event data is auto-deleted 30 days after the event date. You may delete your pitch
            at any time from your profile settings.
          </p>
          <p className="mt-3 text-[14px] text-[#475569] leading-relaxed">
            Detailed legal text is available on request — write to{" "}
            <a className="font-semibold text-[#2563eb]" href="mailto:hello@finally.plus">
              hello@finally.plus
            </a>
            .
          </p>
        </Card>
      </StaticContainer>
    </PublicLayout>
  );
}

export function TermsPage() {
  return (
    <PublicLayout centered={false}>
      <StaticContainer>
        <Logo size="md" />
        <PageHeadline title="Terms of service" className="mt-6" />
        <Card padding="md" className="mt-6">
          <p className="text-[14px] text-[#475569] leading-relaxed">
            By using Finally you agree to follow the platform rules, treat fellow participants
            respectfully, and accept that data will be deleted 30 days after each event.
          </p>
          <p className="mt-3 text-[14px] text-[#475569] leading-relaxed">
            We provide the service "as is" — Finally is a tool for connections, not a guarantee of
            them. If something feels wrong, write to{" "}
            <a className="font-semibold text-[#2563eb]" href="mailto:hello@finally.plus">
              hello@finally.plus
            </a>
            .
          </p>
        </Card>
      </StaticContainer>
    </PublicLayout>
  );
}

/* -------------------------------------------------------------------------
 * Organizer-side aggregated pages reachable from the AppShell.
 * ----------------------------------------------------------------------- */

export function AccountPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState<string | null>(null);
  const [name, setName] = useState<string | null>(null);

  useEffect(() => {
    void supabase.auth.getUser().then(({ data }) => {
      setEmail(data.user?.email ?? null);
      setName((data.user?.user_metadata?.full_name as string | undefined) ?? null);
    });
  }, []);

  const handleSignOut = async () => {
    await signOut();
    navigate("/sign-in");
  };

  return (
    <OrganizerShell>
      <div className="mx-auto max-w-[640px] animate-fade-in">
        <PageHeadline
          title="Account"
          subtitle="Your Finally organizer profile."
        />

        <Card padding="md" className="mt-6">
          <Row label="Name" value={name ?? "—"} />
          <Row label="Email" value={email ?? "—"} />
          <Row label="Plan" value="Free" hint="Upgrade from the Pricing page when you outgrow it." />
        </Card>

        {/* Role switcher renders itself only when the user has 2+ switchable
            roles. For organizers who also signed up as a participant this is
            the one-stop way to jump between the two contexts. */}
        <div className="mt-5">
          <RoleSwitcher />
        </div>

        <Card tone="danger" padding="md" className="mt-5">
          <p className="text-[14px] font-bold text-[#991b1b]">Sign out</p>
          <p className="mt-1 text-[13px] text-[#991b1b]">
            Sign out of this device. You can sign back in at any time.
          </p>
          <Button variant="destructive" onClick={handleSignOut} className="mt-3" size="md">
            Sign out
          </Button>
        </Card>
      </div>
    </OrganizerShell>
  );
}

export function AnalyticsAggregatePage() {
  return (
    <OrganizerShell>
      <div className="mx-auto max-w-[640px] animate-fade-in">
        <PageHeadline
          title="Analytics"
          subtitle="Per-event analytics live inside each event dashboard. A cross-event view is coming."
        />
        <Card padding="md" className="mt-6 text-center">
          <p className="text-[14px] text-[#475569]">
            Open an event from{" "}
            <Link to="/events" className="font-semibold text-[#2563eb]">
              My Events
            </Link>{" "}
            and switch to the Analytics tab for participant timelines, top goals, and engagement.
          </p>
        </Card>
      </div>
    </OrganizerShell>
  );
}

/* ------------------------------------------------------------------------ */

function StaticContainer({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto w-full max-w-[720px] py-8 animate-fade-in">{children}</div>
  );
}

function FaqItem({ q, a }: { q: string; a: string }) {
  return (
    <details className="border-b border-[#e2e8f0] py-3 last:border-0">
      <summary className="cursor-pointer text-[14px] font-bold text-[#1a1a1a]">{q}</summary>
      <p className="mt-2 text-[14px] text-[#475569] leading-relaxed">{a}</p>
    </details>
  );
}

function Row({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="border-b border-[#f1f5f9] py-3 last:border-0">
      <p className="text-[11px] font-bold uppercase tracking-wide text-[#94a3b8]">{label}</p>
      <p className="mt-0.5 text-[14px] text-[#1a1a1a]">{value}</p>
      {hint ? <p className="mt-1 text-[12px] text-[#6c757d]">{hint}</p> : null}
    </div>
  );
}
